import { useMutation } from "@tanstack/react-query";
import {
  type ApiProblemDetails,
  type CreateContentReportRequest,
  type CreateContentReportResult,
  type FetchResponse,
  postSupportContentReports,
  type ProblemDetails,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import {
  VITE_BUILD_NUMBER,
  VITE_VERSION,
} from "Infrastructure/Utils/BuildUtils";
import i18next from "i18next";

/**
 * 400 = validation (keys under `data.errors`), 403 = reporting a chat message without being an
 * accepted member of its channel, 404 = the profile/message is not there (or is a back-office or
 * deactivated persona, which answers the same), 429 = the per-IP limit (no body — branch on
 * `status`), 503 = Brain down AND the Sentry fallback failed.
 */
export type CreateContentReportError =
  | FetchResponse<ApiProblemDetails, 400 | 403 | 404 | 503>
  | FetchResponse<ProblemDetails, 429>;

/** Everything the form knows; the diagnostics fields are filled in here. */
export type CreateContentReportPayload = Omit<
  CreateContentReportRequest,
  "appVersion" | "buildNumber" | "language"
>;

type Props = {
  onSuccess?: (result: CreateContentReportResult) => void;
  onError?: (error: CreateContentReportError) => void;
};

/**
 * Files a DSA Art. 16 notice (ZS-1743) through our API, which forwards it to Mo.one Brain. Same
 * reasons as the support request for going through the backend: Brain's shared secret, and the
 * reporter's identity is taken from the token server-side.
 */
export const useCreateContentReportMutation = ({
  onSuccess,
  onError,
}: Props = {}) =>
  useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      (payload: CreateContentReportPayload) =>
        postSupportContentReports({
          ...payload,
          appVersion: VITE_VERSION ?? "",
          buildNumber: VITE_BUILD_NUMBER ?? "",
          language: i18next.language,
        }),
    ),
    onSuccess,
    onError,
  });
